import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import apiWithAuth from '../../services/apiWithAuth';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { usePermissions } from '../../hooks/usePermissions';

// --- SVG Icon Components for a cleaner UI ---
const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
);

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" />
    </svg>
);

const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const RoleManagement = () => {
    const permissionAccess = usePermissions(['Role Management', 'hr/Employee Management/Role Management']);
    // --- STATE AND DATA FETCHING LOGIC ---
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});
    const [formData, setFormData] = useState({
        name: '', description: '', level: 1, permissions: []
    });

    const { loading, error, makeRequest, clearError } = useAuthenticatedApi();

    const fetchRoles = useCallback(async () => {
        const response = await makeRequest(async () => apiWithAuth.get('/roles'));
        if (response) {
            const sortedRoles = (response.data.data || []).sort((a, b) => a.level - b.level);
            setRoles(sortedRoles);
        } else {
            setRoles([]);
        }
    }, [makeRequest]);

    const fetchPermissions = useCallback(async () => {
        const response = await makeRequest(async () => apiWithAuth.get('/permissions'));
        if (response) {
            setPermissions(response.data || []);
        } else {
            setPermissions([]);
        }
    }, [makeRequest]);

    useEffect(() => {
        Promise.all([fetchRoles(), fetchPermissions()]);
    }, [fetchRoles, fetchPermissions]);

    // --- FORM HANDLERS (LOGIC UNCHANGED) ---
    const resetFormData = () => {
        setFormData({ name: '', description: '', level: 1, permissions: [] });
        setPermissionSearchTerm('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();
        const url = editingRole
            ? `/roles/${editingRole._id}`
            : `/roles`;
        const method = editingRole ? 'put' : 'post';

        const response = await makeRequest(async () => apiWithAuth[method](url, formData));

        if (response) {
            setShowForm(false);
            setEditingRole(null);
            resetFormData();
            fetchRoles();
        }
    };

    const handleEdit = (role) => {
        setEditingRole(role);
        setFormData({
            name: role.name,
            description: role.description,
            level: role.level,
            permissions: role.permissions.map(p => p._id || p)
        });
        setShowForm(true);
    };

    const handleDelete = async (roleId) => {
        if (window.confirm('Are you sure you want to delete this role? This might affect users assigned to it.')) {
            const response = await makeRequest(async () => apiWithAuth.delete(`/roles/${roleId}`));
            if (response) {
                fetchRoles();
            }
        }
    };

    const handlePermissionToggle = (permissionId) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permissionId)
                ? prev.permissions.filter(id => id !== permissionId)
                : [...prev.permissions, permissionId]
        }));
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingRole(null);
        resetFormData();
        clearError();
    };

    // --- FILTERING AND CATEGORIZATION LOGIC ---
    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredPermissions = permissions.filter(p =>
        p.name.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
        p.module.toLowerCase().includes(permissionSearchTerm.toLowerCase())
    );

    // Group permissions by module/category
    const groupedPermissions = filteredPermissions.reduce((acc, permission) => {
        const module = permission.module || 'General';
        if (!acc[module]) {
            acc[module] = [];
        }
        acc[module].push(permission);
        return acc;
    }, {});

    // Get selected permissions with their details
    const selectedPermissions = permissions.filter(p =>
        formData.permissions.includes(p._id)
    );

    const selectedPermissionsByModule = selectedPermissions.reduce((acc, permission) => {
        const module = permission.module || 'General';
        if (!acc[module]) {
            acc[module] = [];
        }
        acc[module].push(permission);
        return acc;
    }, {});

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // --- REDESIGNED JSX ---
    return (
        <div className="bg-gray-100 min-h-screen p-3 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 gap-4">
                    <div>
                        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Role Management</h1>
                        <p className="mt-1 text-sm sm:text-base text-gray-600">Define user roles and assign permissions.</p>
                    </div>
                    <button
                        className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        onClick={() => {
                            setEditingRole(null);
                            resetFormData();
                            setShowForm(true);
                        }}
                    >
                        <PlusIcon />
                        Add New Role
                    </button>
                </header>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            placeholder="Search roles by name or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                {error && !showForm && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                )}

                {/* Roles Table */}
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Loading roles...</div>
                ) : filteredRoles.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                        <h3 className="text-lg font-medium text-gray-900">No Roles Found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? `Your search for "${searchTerm}" did not match any roles.` : 'Click "Add New Role" to create one.'}
                        </p>
                    </div>
                ) : (
                    <>
                    {/* Mobile card view */}
                    <div className="md:hidden space-y-3">
                        {filteredRoles.map((role) => (
                            <div key={role._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="min-w-0">
                                        <div className="text-base font-semibold text-gray-900">{role.name}</div>
                                        <span className="inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            Level {role.level}
                                        </span>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button onClick={() => handleEdit(role)} className="text-indigo-600 hover:text-indigo-900 p-2 rounded hover:bg-indigo-50" title="Edit role">
                                            <EditIcon />
                                        </button>
                                        <button onClick={() => handleDelete(role._id)} className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50" title="Delete role">
                                            <DeleteIcon />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{role.description || 'No description provided.'}</p>
                                <div className="flex flex-wrap gap-1">
                                    {role.permissions?.length > 0 ? (
                                        <>
                                            {role.permissions.slice(0, 3).map(p => (
                                                <span key={p._id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                    {p.name}
                                                </span>
                                            ))}
                                            {role.permissions.length > 3 && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    +{role.permissions.length - 3} more
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-xs text-gray-500">No permissions</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredRoles.map((role) => (
                                        <tr key={role._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{role.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                    Level {role.level}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900 max-w-xs truncate">
                                                    {role.description || 'No description provided.'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {role.permissions?.length > 0 ? (
                                                        <>
                                                            {role.permissions.slice(0, 2).map(p => (
                                                                <span key={p._id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                    {p.name}
                                                                </span>
                                                            ))}
                                                            {role.permissions.length > 2 && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                                    +{role.permissions.length - 2} more
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-gray-500">No permissions</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(role)}
                                                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                                                        title="Edit role"
                                                    >
                                                        <EditIcon />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(role._id)}
                                                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                                        title="Delete role"
                                                    >
                                                        <DeleteIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    </>
                )}
            </div>

            {/* Add/Edit Role Modal */}
            {showForm && createPortal(
            <>
            <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] transition-opacity duration-300" onClick={handleCancelForm}></div>
            <div className="fixed inset-0 z-[10001] flex justify-center items-center p-3 sm:p-4 transition-all duration-300">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                    <header className="flex items-center justify-between p-4 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
                        <button onClick={handleCancelForm} className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"><CloseIcon /></button>
                    </header>
                    <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                        <div className="p-6 overflow-y-auto">
                            {error && (
                                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert"><p>{error}</p></div>
                            )}

                            {/* Basic Role Info */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                                <div className="lg:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                                    <input type="number" value={formData.level} onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) || 1 })} min="1" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"></textarea>
                            </div>

                            {/* Permissions Section */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Available Permissions */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Available Permissions</label>
                                    <div className="relative mb-3">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <SearchIcon />
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search permissions..."
                                            value={permissionSearchTerm}
                                            onChange={(e) => setPermissionSearchTerm(e.target.value)}
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div className="border rounded-md max-h-96 overflow-y-auto bg-gray-50">
                                        {Object.keys(groupedPermissions).length === 0 ? (
                                            <p className="text-center text-gray-500 p-4">No permissions found.</p>
                                        ) : (
                                            Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
                                                <div key={module} className="border-b border-gray-200 last:border-b-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleCategory(module)}
                                                        className="w-full px-4 py-3 text-left bg-gradient-to-r from-gray-100 to-gray-50 hover:from-gray-200 hover:to-gray-100 flex items-center justify-between font-semibold text-gray-800 border-l-4 border-indigo-500"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-indigo-600">
                                                                {expandedCategories[module] ? (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                    </svg>
                                                                )}
                                                            </span>
                                                            <span className="capitalize text-sm font-bold">{module.replace(/_/g, ' ')}</span>
                                                        </div>
                                                        <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full">
                                                            {modulePermissions.length}
                                                        </span>
                                                    </button>
                                                    {expandedCategories[module] && (
                                                        <div className="bg-white border-l-4 border-indigo-200">
                                                            {modulePermissions.map((permission) => (
                                                                <label key={permission._id} className="flex items-start gap-3 pl-8 pr-4 py-3 cursor-pointer hover:bg-indigo-50 border-t border-gray-100 relative">
                                                                    {/* Indentation line */}
                                                                    <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-300"></div>
                                                                    <div className="absolute left-5 top-4 w-3 h-px bg-gray-300"></div>

                                                                    <input
                                                                        type="checkbox"
                                                                        checked={formData.permissions.includes(permission._id)}
                                                                        onChange={() => handlePermissionToggle(permission._id)}
                                                                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5"
                                                                    />
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium text-gray-800">{permission.name}</span>
                                                                            {Array.isArray(permission.action) && permission.action.length > 0 && (
                                                                                <div className="flex gap-1">
                                                                                    {permission.action.map((action, actionIndex) => (
                                                                                        <span key={actionIndex} className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded font-medium">
                                                                                            {action.toUpperCase()}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-gray-500 text-xs mt-1 block">{permission.description || 'No description available'}</span>
                                                                    </div>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* Selected Permissions */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Selected Permissions ({selectedPermissions.length})
                                    </label>
                                    <div className="border rounded-md max-h-96 overflow-y-auto bg-green-50">
                                        {selectedPermissions.length === 0 ? (
                                            <p className="text-center text-gray-500 p-4">No permissions selected.</p>
                                        ) : (
                                            Object.entries(selectedPermissionsByModule).map(([module, modulePermissions]) => (
                                                <div key={module} className="border-b border-green-200 last:border-b-0">
                                                    <div className="px-4 py-3 bg-gradient-to-r from-green-100 to-green-50 font-semibold text-green-800 capitalize flex items-center justify-between border-l-4 border-green-500">
                                                        <div className="flex items-center gap-2">
                                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            <span className="text-sm font-bold">{module.replace(/_/g, ' ')}</span>
                                                        </div>
                                                        <span className="bg-green-200 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                                            {modulePermissions.length}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white border-l-4 border-green-200">
                                                        {modulePermissions.map((permission) => (
                                                            <div key={permission._id} className="flex items-start justify-between pl-8 pr-4 py-3 border-t border-green-100 hover:bg-green-50 relative">
                                                                {/* Indentation line */}
                                                                <div className="absolute left-6 top-0 bottom-0 w-px bg-green-300"></div>
                                                                <div className="absolute left-5 top-4 w-3 h-px bg-green-300"></div>

                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-gray-800">{permission.name}</span>
                                                                        {Array.isArray(permission.action) && permission.action.length > 0 && (
                                                                            <div className="flex gap-1">
                                                                                {permission.action.map((action, actionIndex) => (
                                                                                    <span key={actionIndex} className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded font-medium">
                                                                                        {action.toUpperCase()}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-gray-500 text-xs mt-1 block">{permission.description || 'No description available'}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionToggle(permission._id)}
                                                                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 ml-2 flex-shrink-0"
                                                                    title="Remove permission"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <footer className="flex gap-3 justify-end p-4 bg-gray-50 border-t mt-auto">
                            <button type="button" onClick={handleCancelForm} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">{editingRole ? 'Update Role' : 'Create Role'}</button>
                        </footer>
                    </form>
                </div>
            </div>
            </>,
            document.body
            )}
        </div>
    );
};

export default RoleManagement;